/**
 * Precision Price Display Module
 * ==============================
 * 
 * Shared utilities for updating the main price display on product pages.
 * The price display shows the PER-UNIT price with applicable discounts.
 * 
 * @module components/precision-price-display
 * 
 * ## HTML Structure Expected
 * ```html
 * <div data-precision-price-display>
 *   <p data-current-price>$44.00</p>
 *   <p data-compare-price class="tw-hidden">$52.00</p>
 * </div>
 * ```
 * 
 * ## Usage
 * ```javascript
 * import { updatePriceDisplay, getPriceDisplayContainer } from './precision-price-display.js';
 * 
 * const container = getPriceDisplayContainer();
 * updatePriceDisplay({
 *   container,
 *   currentPrice: 3960,  // $39.60 in cents
 *   comparePrice: 4400,  // $44.00 in cents
 *   moneyFormat: '${{amount}}',
 *   showCompare: true
 * });
 * ```
 */

import { formatMoney } from '../utils/format-money.js';

/**
 * Update price display elements with current and compare prices
 * 
 * @param {Object} options - Update options
 * @param {HTMLElement} options.container - Price display container element
 * @param {number} options.currentPrice - Current/discounted price in cents
 * @param {number} [options.comparePrice=null] - Original/compare price in cents (shown with strikethrough)
 * @param {string} [options.moneyFormat='${{amount}}'] - Shopify money format string
 * @param {boolean} [options.showCompare=false] - Whether to show the compare price
 * 
 * @example
 * // Show discounted price with compare
 * updatePriceDisplay({
 *   container: document.querySelector('[data-precision-price-display]'),
 *   currentPrice: 3740,  // $37.40 (after 15% discount)
 *   comparePrice: 4400,  // $44.00 (original)
 *   moneyFormat: '${{amount}}',
 *   showCompare: true
 * });
 */
export function updatePriceDisplay({
  container,
  currentPrice,
  comparePrice = null,
  moneyFormat = '${{amount}}',
  showCompare = false
}) {
  if (!container) return;

  const currentPriceEl = container.querySelector('[data-current-price]');
  const comparePriceEl = container.querySelector('[data-compare-price]');

  // Update current price
  if (currentPriceEl) {
    currentPriceEl.textContent = formatMoney(currentPrice, moneyFormat);
  }

  // Update compare price (strikethrough)
  if (comparePriceEl) {
    if (showCompare && comparePrice && comparePrice > currentPrice) {
      comparePriceEl.textContent = formatMoney(comparePrice, moneyFormat);
      comparePriceEl.classList.remove('tw-hidden');
    } else {
      comparePriceEl.classList.add('tw-hidden');
    }
  }
}

/**
 * Get the price display container element
 * 
 * @returns {HTMLElement|null} The price display container or null if not found
 */
export function getPriceDisplayContainer() {
  return document.querySelector('[data-precision-price-display]');
}

/**
 * Dispatch a custom price changed event
 * 
 * @param {HTMLElement} element - Element to dispatch the event from
 * @param {Object} detail - Event detail object
 */
export function dispatchPriceChanged(element, detail) {
  const event = new CustomEvent('price:changed', {
    detail,
    bubbles: true
  });
  element.dispatchEvent(event);
}
